import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';

const SEND_ERRORS: Record<string, string> = {
  project_mail_not_configured: 'Šio projekto el. pašto siuntimas nenustatytas. Užpildykite SMTP laukus skiltyje Projektai.',
  mail_failed: 'Laiško išsiųsti nepavyko. Patikrinkite projekto SMTP nustatymus.',
  pdf_busy: 'Šiuo metu ruošiama daug PDF. Bandykite po minutės.',
};

/** "Send by e-mail": recipient (the buyer's address by default), optional subject and message. Sending is the only way an invoice leaves. */
export function SendInvoiceDialog({ invoice, onClose, reminder = false, quote = false }: { invoice: { id: number; label: string; email?: string } | null; onClose: () => void; reminder?: boolean; quote?: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [done, setDone] = useState(false);
  const send = useMutation({
    mutationFn: (body: { to: string; subject?: string; message?: string }) => api.post(quote ? `api/v1/quotes/${invoice!.id}/send` : `api/v1/invoices/${invoice!.id}/${reminder ? 'remind' : 'send'}`, body),
    onSuccess: async () => { setDone(true); await Promise.all(['invoices', 'dashboard', 'receivables', 'quotes', 'quote', 'mail-log'].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))); },
  });
  useEffect(() => {
    if (invoice) { setDone(false); send.reset(); dialog.current?.showModal(); } else dialog.current?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    const subject = String(d.get('subject') ?? '').trim();
    const message = String(d.get('message') ?? '').trim();
    send.mutate({ to: String(d.get('to')), ...(subject ? { subject } : {}), ...(message ? { message } : {}) });
  };
  const code = send.error instanceof ApiError ? send.error.code : '';
  return (
    <dialog ref={dialog} className="confirm-dialog send-dialog" onClose={onClose}>
      {invoice ? (
        <form className="dialog-card" onSubmit={submit}>
          <p className="eyebrow">{reminder ? t('Priminimas apie mokėjimą') : t('Siuntimas el. paštu')}</p>
          <h2>{quote ? t('Pasiūlymas') : t('Sąskaita')} {invoice.label}</h2>
          {done ? <Alert kind="success">{reminder ? t('Priminimas išsiųstas.') : quote ? t('Pasiūlymas išsiųstas.') : t('Sąskaita išsiųsta.')}</Alert> : null}
          {send.isError ? <Alert kind="error">{SEND_ERRORS[code] ? t(SEND_ERRORS[code]) : errorText(t, send.error)}</Alert> : null}
          {!done ? (
            <>
              <label>{t('Gavėjas')}<input type="email" name="to" defaultValue={invoice.email ?? ''} required autoFocus /></label>
              <label>{t('Tema')}<input name="subject" placeholder={reminder ? `Priminimas: sąskaita ${invoice.label}` : quote ? `Komercinis pasiūlymas ${invoice.label}` : `${t('Sąskaita')} ${invoice.label}`} /></label>
              <label>{t('Žinutė')}<textarea name="message" rows={4} placeholder={reminder ? t('Jei tuščia — priminimo tekstas iš nustatymų') : t('Jei tuščia — standartinis tekstas')} /></label>
              <p className="muted">{reminder ? t('PDF bus pridėtas prie laiško.') : t('PDF bus pridėtas prie laiško. Juodraštis taps išsiųstu.')}</p>
            </>
          ) : null}
          <div className="dialog-actions">
            <button type="button" className="button button--ghost" onClick={onClose}>{done ? t('Uždaryti') : t('Atšaukti')}</button>
            {!done ? <button type="submit" className="button button--primary" disabled={send.isPending}>{send.isPending ? t('Siunčiama…') : t('Siųsti')}</button> : null}
          </div>
        </form>
      ) : null}
    </dialog>
  );
}

interface MailLogRow { recipient: string; subject: string; status: 'sent' | 'failed'; createdAt: string; detail: string | null; purpose: 'invoice' | 'reminder' | 'quote' }

/** Siuntimų istorija: every e-mail of the invoice (the invoice, reminders), when, to whom, and why one did not go. */
export function MailHistory({ invoiceId }: { invoiceId: number }) {
  const { t, locale } = useI18n();
  const log = useQuery({ queryKey: ['mail-log', invoiceId], queryFn: () => api.get<{ log: MailLogRow[] }>(`api/v1/invoices/${invoiceId}/mail-log`).then((r) => r.log) });
  if (!log.data?.length) return null;
  const when = (iso: string) => new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <section className="panel-card mail-history">
      <p className="eyebrow">{t('Siuntimų istorija')}</p>
      <ul>
        {log.data.map((m, i) => (
          <li key={i}>
            <span className="mail-history__when">{when(m.createdAt)}</span>
            <span className="mail-history__to">{m.recipient}{m.purpose === 'reminder' ? ` · ${t('Priminimas')}` : ''}</span>
            {m.status === 'sent' ? <span className="badge badge--ok">{t('Išsiųsta')} ✓</span>
              : <span className="badge badge--error" title={m.detail ?? ''}>{t('Neišsiųsta')}</span>}
            {m.status === 'failed' && m.detail ? <small className="mail-history__why">{m.detail}</small> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

